import type {
  LocaleCode,
  LocalizedString,
  RiskAdjustmentAnswer,
  RiskAdjustmentQuestion,
  RiskModelWithDetails,
} from "@repo/shared/types";
import { isValidAdjustmentDelta, MAX_RISK_ADJUSTMENT_QUESTIONS } from "@repo/shared/types";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { TranslationButton } from "@/components/ui/translation-button";
import { ApiError } from "@/lib/api/_client";
import { useUpdateRiskModel } from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { getScoreRange } from "@/lib/utils/risk-scoring";
import { T } from "./risk-adjustment-questions-editor.i18n";

type Props = {
  model: RiskModelWithDetails;
  onError: (title: string, description: string) => void;
  /** Reports whether the editor holds unsaved changes (drives the parent tab guard). */
  onDirtyChange?: (dirty: boolean) => void;
};

/**
 * QP-180 — editor for a risk model's config-driven adjustment questions.
 *
 * QL-144 — draft-based editing with an explicit save: every edit stays local
 * until Save PATCHes the whole adjustmentQuestions array via the model
 * endpoint. Invalid drafts (missing labels, bad deltas) block the save with
 * inline errors instead of being silently deferred, and the dirty state is
 * reported upward so leaving the tab can be confirmed rather than silently
 * discarding work. Columns of the delta matrix are the model's initial-score
 * values (min..max of its scoring matrix).
 */
export function RiskAdjustmentQuestionsEditor({ model, onError, onDirtyChange }: Props) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const updateModel = useUpdateRiskModel();

  // The model's possible initial-score values (matrix min..max).
  const scoreValues = useMemo(() => {
    const range = getScoreRange(model.matrixScores);
    const out: number[] = [];
    for (let v = Math.ceil(range.min); v <= Math.floor(range.max); v++) out.push(v);
    return out;
  }, [model.matrixScores]);

  const [questions, setQuestions] = useState<RiskAdjustmentQuestion[]>(
    () => model.adjustmentQuestions ?? [],
  );
  // QL-144 — the clean baseline the draft is compared against (serialized so
  // dirty checking is a plain string comparison of this nested structure).
  const [baselineJson, setBaselineJson] = useState<string>(() =>
    JSON.stringify(model.adjustmentQuestions ?? []),
  );
  const [isSaving, setIsSaving] = useState(false);
  // Validation errors are surfaced on a save attempt, not while typing.
  const [showErrors, setShowErrors] = useState(false);

  const hasAnyLabel = (i18n: LocalizedString) =>
    Object.values(i18n ?? {}).some((v) => (v ?? "").trim() !== "");

  const isDirty = useMemo(
    () => JSON.stringify(questions) !== baselineJson,
    [questions, baselineJson],
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Adopt server state (e.g. after an external refetch) only while the editor
  // is clean, so a refetch never clobbers an in-progress draft.
  useEffect(() => {
    const server = JSON.stringify(model.adjustmentQuestions ?? []);
    if (!isDirty && server !== baselineJson) {
      setQuestions(model.adjustmentQuestions ?? []);
      setBaselineJson(server);
    }
  }, [model.adjustmentQuestions, isDirty, baselineJson]);

  // Warn before the browser itself would discard a dirty draft (refresh/close).
  useEffect(() => {
    if (!isDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const updateQuestion = (id: string, patch: Partial<RiskAdjustmentQuestion>) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const updateAnswer = (
    questionId: string,
    answerId: string,
    patch: Partial<RiskAdjustmentAnswer>,
  ) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? { ...q, answers: q.answers.map((a) => (a.id === answerId ? { ...a, ...patch } : a)) }
          : q,
      ),
    );
  };

  const addQuestion = () => {
    if (questions.length >= MAX_RISK_ADJUSTMENT_QUESTIONS) return;
    setQuestions([
      ...questions,
      {
        id: crypto.randomUUID(),
        // QL-144 — a freshly added question starts disabled until the designer
        // finishes and enables it, like other entities that start inactive.
        enabled: false,
        labelI18n: {},
        answers: [{ id: crypto.randomUUID(), labelI18n: {}, deltas: {} }],
      },
    ]);
  };

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const addAnswer = (questionId: string) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              answers: [...q.answers, { id: crypto.randomUUID(), labelI18n: {}, deltas: {} }],
            }
          : q,
      ),
    );
  };

  const removeAnswer = (questionId: string, answerId: string) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId ? { ...q, answers: q.answers.filter((a) => a.id !== answerId) } : q,
      ),
    );
  };

  // QL-8 — deltas allow one decimal place. The inputs are controlled from the
  // numeric `deltas`, which would erase intermediate text like "1." on
  // re-render, so the raw string is kept in a per-cell draft while editing.
  // Drafts fold into the question draft on blur; only values passing the
  // shared server rule are committed; invalid drafts (e.g. 1.25) stay local
  // with an inline error and block Save.
  const [deltaDrafts, setDeltaDrafts] = useState<Record<string, string>>({});

  const draftKey = (questionId: string, answerId: string, score: number) =>
    `${questionId}:${answerId}:${score}`;

  const isDraftInvalid = (raw: string | undefined): boolean => {
    if (raw === undefined) return false;
    const trimmed = raw.trim();
    if (trimmed === "" || trimmed === "-") return false;
    const n = Number(trimmed);
    return Number.isNaN(n) || !isValidAdjustmentDelta(n);
  };

  const questionHasInvalidDelta = (questionId: string) =>
    Object.entries(deltaDrafts).some(
      ([key, raw]) => key.startsWith(`${questionId}:`) && isDraftInvalid(raw),
    );

  const setDelta = (questionId: string, answerId: string, score: number, raw: string) => {
    setDeltaDrafts((prev) => ({ ...prev, [draftKey(questionId, answerId, score)]: raw }));
  };

  /**
   * Commit the cell's draft: persist a valid value (or an emptied cell as a
   * deletion) and drop the draft; an invalid draft stays with its inline error.
   */
  const commitDeltaDraft = (questionId: string, answerId: string, score: number) => {
    const key = draftKey(questionId, answerId, score);
    const raw = deltaDrafts[key];
    if (raw === undefined || isDraftInvalid(raw)) return;
    setDeltaDrafts((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
    const trimmed = raw.trim();
    const next = trimmed === "" || trimmed === "-" ? null : Number(trimmed);
    const current =
      questions.find((q) => q.id === questionId)?.answers.find((a) => a.id === answerId)?.deltas?.[
        String(score)
      ] ?? null;
    if (next === current) return;
    updateAnswerDelta(questionId, answerId, score, trimmed);
  };

  const updateAnswerDelta = (questionId: string, answerId: string, score: number, raw: string) => {
    const key = String(score);
    setQuestions(
      questions.map((q) => {
        if (q.id !== questionId) return q;
        return {
          ...q,
          answers: q.answers.map((a) => {
            if (a.id !== answerId) return a;
            const deltas = { ...a.deltas };
            if (raw === "" || raw === "-") {
              delete deltas[key];
            } else {
              const n = Number(raw);
              if (Number.isNaN(n)) delete deltas[key];
              else deltas[key] = n;
            }
            return { ...a, deltas };
          }),
        };
      }),
    );
  };

  // QL-144 — the server rejects questions/answers without labels; surface that
  // as inline errors on a save attempt instead of silently deferring the PATCH.
  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    questions.forEach((q, qi) => {
      if (!hasAnyLabel(q.labelI18n)) {
        errors.push(t.questionLabelRequired(String(qi + 1)));
      }
      q.answers.forEach((a, ai) => {
        if (!hasAnyLabel(a.labelI18n)) {
          errors.push(t.answerLabelRequired(String(qi + 1), String(ai + 1)));
        }
      });
    });
    if (Object.values(deltaDrafts).some((raw) => isDraftInvalid(raw))) {
      errors.push(t.invalidDelta);
    }
    return errors;
  }, [questions, deltaDrafts, t]);

  const handleSave = async () => {
    if (validationErrors.length > 0) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setIsSaving(true);
    try {
      await updateModel.mutateAsync({
        id: model.id,
        data: {
          nameI18n: model.nameI18n,
          descriptionI18n: model.descriptionI18n ?? undefined,
          adjustmentQuestions: questions,
        },
      });
      setBaselineJson(JSON.stringify(questions));
    } catch (err) {
      // Keep the draft so nothing is lost; the user can fix and retry.
      onError(t.error, err instanceof ApiError ? err.message : t.failedToSave);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setQuestions(JSON.parse(baselineJson));
    setDeltaDrafts({});
    setShowErrors(false);
  };

  if (scoreValues.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          {t.matrixHint}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground">{t.description}</p>
      </div>

      {questions.map((question, qIndex) => (
        <Card key={question.id}>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="text-base">
                {t.questionLabel} {qIndex + 1}
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={question.enabled}
                    onCheckedChange={(checked) => updateQuestion(question.id, { enabled: checked })}
                    aria-label={t.enabled}
                    id={`q-enabled-${question.id}`}
                  />
                  <Label htmlFor={`q-enabled-${question.id}`} className="font-normal">
                    {t.enabled}
                  </Label>
                </div>
                <Button
                  size="icon"
                  tooltip={t.remove}
                  variant="ghost"
                  onClick={() => removeQuestion(question.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Question label */}
            <div className="space-y-2">
              <Label>{t.questionLabel}</Label>
              <div className="flex items-center gap-1">
                <Input
                  value={(question.labelI18n as LocalizedString)[userLanguage as LocaleCode] ?? ""}
                  onChange={(e) =>
                    setQuestions((prev) =>
                      prev.map((q) =>
                        q.id === question.id
                          ? { ...q, labelI18n: { ...q.labelI18n, [userLanguage]: e.target.value } }
                          : q,
                      ),
                    )
                  }
                  className={cn(
                    showErrors && !hasAnyLabel(question.labelI18n) && "border-destructive",
                  )}
                  aria-invalid={(showErrors && !hasAnyLabel(question.labelI18n)) || undefined}
                  placeholder={t.questionPlaceholder}
                />
                <TranslationButton
                  value={question.labelI18n as LocalizedString}
                  onSave={(value) => updateQuestion(question.id, { labelI18n: value })}
                  supportedLanguages={tenantSupportedLanguages}
                />
              </div>
            </div>

            {/* Answers + delta matrix */}
            <div className="space-y-2">
              <Label>{t.matrix}</Label>
              <p className="text-xs text-muted-foreground">{t.matrixColHint}</p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border p-2 bg-muted text-left font-semibold min-w-[220px]">
                        {t.answerColumn}
                      </th>
                      {scoreValues.map((score) => (
                        <th
                          key={score}
                          className="border p-2 bg-muted text-center font-semibold w-16"
                        >
                          {score}
                        </th>
                      ))}
                      <th className="border p-2 bg-muted w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {question.answers.map((answer) => (
                      <tr key={answer.id}>
                        <td className="border p-2 align-top">
                          <div className="flex items-center gap-1">
                            <Input
                              value={
                                (answer.labelI18n as LocalizedString)[userLanguage as LocaleCode] ??
                                ""
                              }
                              onChange={(e) =>
                                setQuestions((prev) =>
                                  prev.map((q) =>
                                    q.id === question.id
                                      ? {
                                          ...q,
                                          answers: q.answers.map((a) =>
                                            a.id === answer.id
                                              ? {
                                                  ...a,
                                                  labelI18n: {
                                                    ...a.labelI18n,
                                                    [userLanguage]: e.target.value,
                                                  },
                                                }
                                              : a,
                                          ),
                                        }
                                      : q,
                                  ),
                                )
                              }
                              className={cn(
                                showErrors &&
                                  !hasAnyLabel(answer.labelI18n) &&
                                  "border-destructive",
                              )}
                              aria-invalid={
                                (showErrors && !hasAnyLabel(answer.labelI18n)) || undefined
                              }
                              placeholder={t.answerPlaceholder}
                            />
                            <TranslationButton
                              value={answer.labelI18n as LocalizedString}
                              onSave={(value) =>
                                updateAnswer(question.id, answer.id, { labelI18n: value })
                              }
                              supportedLanguages={tenantSupportedLanguages}
                            />
                          </div>
                        </td>
                        {scoreValues.map((score) => {
                          const invalid = isDraftInvalid(
                            deltaDrafts[draftKey(question.id, answer.id, score)],
                          );
                          return (
                            <td key={score} className="border p-1 text-center">
                              <Input
                                type="number"
                                step={0.1}
                                className={cn(
                                  "w-14 text-center px-1",
                                  invalid && "border-destructive",
                                )}
                                aria-invalid={invalid || undefined}
                                aria-label={`${localize(answer.labelI18n) || "answer"} @ ${score}`}
                                value={
                                  deltaDrafts[draftKey(question.id, answer.id, score)] ??
                                  answer.deltas?.[String(score)] ??
                                  ""
                                }
                                onChange={(e) =>
                                  setDelta(question.id, answer.id, score, e.target.value)
                                }
                                onBlur={() => commitDeltaDraft(question.id, answer.id, score)}
                              />
                            </td>
                          );
                        })}
                        <td className="border p-1 text-center">
                          {question.answers.length > 1 && (
                            <Button
                              size="icon"
                              tooltip={t.remove}
                              variant="ghost"
                              onClick={() => removeAnswer(question.id, answer.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {questionHasInvalidDelta(question.id) && (
                <p className="text-xs text-destructive">{t.invalidDelta}</p>
              )}
              <Button size="sm" variant="outline" onClick={() => addAnswer(question.id)}>
                <Plus className="h-4 w-4 mr-1" />
                {t.addAnswer}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <Button
        variant="outline"
        onClick={addQuestion}
        disabled={questions.length >= MAX_RISK_ADJUSTMENT_QUESTIONS}
      >
        <Plus className="h-4 w-4 mr-1" />
        {t.addQuestion}
      </Button>

      {/* QL-144 — explicit save with surfaced validation */}
      {showErrors && validationErrors.length > 0 && (
        <div className="space-y-1">
          {validationErrors.map((error) => (
            <p key={error} className="text-xs text-destructive">
              {error}
            </p>
          ))}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={handleDiscard} disabled={!isDirty || isSaving}>
          {t.discardChanges}
        </Button>
        <Button onClick={handleSave} disabled={!isDirty} loading={isSaving}>
          {t.save}
        </Button>
      </div>
    </div>
  );
}
